/**
 * Panel de configuración fiscal ARCA/AFIP (homologación vs producción).
 *
 * Usado en first-run y en Opciones. No implementa emisión de comprobantes;
 * solo persistencia (SystemConfig + media/arca_certs) y pruebas WSAA/WSFE.
 * Textos guía alineados a documentación pública AFIP (WSFEv1); ver docs/RUNBOOK.md (detalle: docs/archive/ARCA_AFIP_CONFIG.md).
 */

import React, { useCallback, useEffect, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import { Label } from "@/components/ui/label";
import {
  arcaAfipAPI,
  type ArcaAfipEnvSnapshot,
  type ArcaAfipStatusResponse,
  type ArcaEnvironment,
} from "@/lib/api";
import { toast } from "sonner";
import { Info, Loader2, RefreshCw } from "lucide-react";

const LINKS = {
  wsfeHomo: "https://wswhomo.afip.gob.ar/wsfev1/service.asmx",
  wsfeProd: "https://servicios1.afip.gov.ar/wsfev1/service.asmx",
  afipFe: "https://www.afip.gob.ar/fe/",
} as const;

type Props = {
  /** CUIT del paso Empresa (first-run); en Opciones se omite y usa CompanyConfig en backend. */
  cuitDraft?: string;
  variant: "firstRun" | "opciones";
};

/** Entorno persistido en servidor (camelCase DRF o snake_case JSON plano). */
function activeEnvironmentFromStatus(s: ArcaAfipStatusResponse): ArcaEnvironment {
  const raw =
    (s as { activeEnvironment?: string }).activeEnvironment ??
    (s as { active_environment?: string }).active_environment;
  return raw === "produccion" ? "produccion" : "homologacion";
}

export function ArcaAfipWizardPanel({ cuitDraft, variant }: Props) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<ArcaAfipStatusResponse | null>(null);
  const [tab, setTab] = useState<ArcaEnvironment>("homologacion");
  const [activeEnv, setActiveEnv] = useState<ArcaEnvironment>("homologacion");
  const [puntoVenta, setPuntoVenta] = useState(1);
  const [certFile, setCertFile] = useState<File | null>(null);
  const [keyFile, setKeyFile] = useState<File | null>(null);
  const [ptosList, setPtosList] = useState<Array<{ nro?: number; emisionTipo?: string; bloqueado?: string }>>([]);

  const cuitParam = variant === "firstRun" && cuitDraft?.trim() ? cuitDraft.trim() : undefined;

  const anonFirstRun = variant === "firstRun";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await arcaAfipAPI.status(
        cuitParam ? { cuit: cuitParam } : undefined,
        anonFirstRun ? { omitAuth: true } : undefined
      );
      if ((s as unknown as { degraded?: boolean }).degraded) {
        toast.message(
          (s as unknown as { error?: string }).error ||
            "Estado fiscal parcial: revisá permisos de media o SystemConfig."
        );
      }
      setStatus(s);
      const ae = activeEnvironmentFromStatus(s);
      setActiveEnv(ae);
      setTab(ae);
      const snap = s.environments[ae] as ArcaAfipEnvSnapshot | undefined;
      if (snap) setPuntoVenta(Number(snap.puntoVenta) || 1);
    } catch (e: unknown) {
      console.error(e);
      toast.error("No se pudo cargar el estado fiscal");
    } finally {
      setLoading(false);
    }
  }, [cuitParam, anonFirstRun]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!status) return;
    const snap = status.environments[tab];
    if (snap) setPuntoVenta(Number(snap.puntoVenta) || 1);
  }, [tab, status]);

  const snapForTab = status?.environments[tab];
  const envInUseCaE = status ? activeEnvironmentFromStatus(status) : null;

  const savePuntoYActivo = async () => {
    setBusy(true);
    try {
      await arcaAfipAPI.patchSettings(
        {
          activeEnvironment: activeEnv,
          [tab]: { puntoVenta },
        },
        anonFirstRun ? { omitAuth: true } : undefined
      );
      toast.success("Configuración guardada");
      await load();
    } catch (e: unknown) {
      const msg = (e as { message?: string }).message || String(e);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const upload = async () => {
    if (!certFile || !keyFile) {
      toast.error("Seleccioná certificado (.crt/.pem) y clave privada (.key)");
      return;
    }
    setBusy(true);
    try {
      const r = await arcaAfipAPI.uploadCertificate(
        tab,
        certFile,
        keyFile,
        anonFirstRun ? { omitAuth: true } : undefined
      );
      if (!r.ok) {
        toast.error(r.error || "Error al subir archivos");
        return;
      }
      toast.success("Certificado y clave guardados");
      setCertFile(null);
      setKeyFile(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const validate = async () => {
    setBusy(true);
    try {
      const r = await arcaAfipAPI.validate(
        {
          environment: tab,
          ...(cuitParam ? { cuit: cuitParam } : {}),
        },
        anonFirstRun ? { omitAuth: true } : undefined
      );
      if (r.ok) {
        toast.success(r.message || "Conexión OK");
      } else {
        toast.error(r.error || r.message || "Fallo la validación");
      }
    } catch (e: unknown) {
      const msg = (e as { message?: string }).message || String(e);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const listarPv = async () => {
    setBusy(true);
    try {
      const r = await arcaAfipAPI.puntosVenta(
        {
          environment: tab,
          ...(cuitParam ? { cuit: cuitParam } : {}),
        },
        anonFirstRun ? { omitAuth: true } : undefined
      );
      if (!r.ok) {
        setPtosList([]);
        const detail =
          r.error ||
          (r.wsfeErrors && r.wsfeErrors.length
            ? r.wsfeErrors.map((x) => x.msg).join(" · ")
            : "No se pudieron listar puntos de venta (revisá certificado y autorización WSFE).");
        toast.error(detail);
        return;
      }
      setPtosList(r.puntosVenta || []);
      toast.success(`${(r.puntosVenta || []).length} punto(s) de venta`);
    } catch (e: unknown) {
      const msg = (e as { message?: string }).message || String(e);
      toast.error(msg);
      setPtosList([]);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !status) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4 text-sm text-slate-700 dark:text-slate-200">
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant={tab === "homologacion" ? "default" : "outline"}
          onClick={() => setTab("homologacion")}
        >
          Homologación (test)
          {envInUseCaE === "homologacion" && (
            <Badge variant="secondary" className="ml-0.5 text-[10px] px-1.5 py-0 font-normal">
              En uso (CAE)
            </Badge>
          )}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={tab === "produccion" ? "default" : "outline"}
          onClick={() => setTab("produccion")}
        >
          Producción
          {envInUseCaE === "produccion" && (
            <Badge variant="secondary" className="ml-0.5 text-[10px] px-1.5 py-0 font-normal">
              En uso (CAE)
            </Badge>
          )}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => load()} disabled={busy}>
          <RefreshCw className={`h-4 w-4 mr-1 ${busy ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      {envInUseCaE != null && (
        <Alert
          className={
            envInUseCaE === "produccion"
              ? "border-orange-300 bg-orange-50/90 text-orange-950 dark:border-orange-800 dark:bg-orange-950/35 dark:text-orange-100 [&>svg]:text-orange-700 dark:[&>svg]:text-orange-300"
              : "border-sky-300 bg-sky-50/90 text-sky-950 dark:border-sky-800 dark:bg-sky-950/35 dark:text-sky-100 [&>svg]:text-sky-700 dark:[&>svg]:text-sky-300"
          }
        >
          <Info className="size-4 shrink-0" aria-hidden />
          <AlertTitle>
            Entorno en uso para CAE:{" "}
            <strong>{envInUseCaE === "produccion" ? "Producción" : "Homologación (test)"}</strong>
          </AlertTitle>
          <AlertDescription className="text-current/90">
            <p>
              {envInUseCaE === "produccion"
                ? "Las solicitudes de CAE van contra AFIP producción. Cambiá el entorno activo abajo solo si sabés lo que hacés."
                : "Las solicitudes de CAE van contra el ambiente de pruebas de AFIP; los comprobantes no tienen validez fiscal."}
            </p>
            {tab !== envInUseCaE && (
              <p className="mt-2 text-xs opacity-90">
                Estás viendo la pestaña de{" "}
                <strong>{tab === "produccion" ? "producción" : "homologación"}</strong>; la facturación sigue usando{" "}
                <strong>{envInUseCaE === "produccion" ? "producción" : "homologación"}</strong> hasta que guardes otro
                entorno activo.
              </p>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2 bg-slate-50/50 dark:bg-slate-900/30">
        <p className="font-medium">Qué hace la app</p>
        <ul className="list-disc list-inside text-xs space-y-1 text-slate-600 dark:text-slate-400">
          <li>Guarda certificado y clave por entorno (archivos en el servidor, carpeta segura de media).</li>
          <li>Guarda en base de datos el entorno activo y el punto de venta fiscal usado en WSFE.</li>
          <li>Prueba WSAA + método WSFE oficial FEParamGetPtosVenta para listar PV habilitados.</li>
        </ul>
        <p className="font-medium pt-2">Qué hacés vos en ARCA / AFIP (manual)</p>
        <ul className="list-disc list-inside text-xs space-y-1 text-slate-600 dark:text-slate-400">
          <li>
            Obtener certificado digital y clave privada para el CUIT emisor (en homologación usá el circuito de testing de
            AFIP; en producción el certificado de producción).
          </li>
          <li>Autorizar el servicio &quot;Facturación Electrónica&quot; (WSFE) para ese certificado.</li>
          <li>
            Dar de alta y habilitar el punto de venta fiscal en la web de AFIP si aún no existe; la app no crea PV en
            AFIP.
          </li>
        </ul>
        <p className="text-xs text-slate-500 pt-1">
          Referencias públicas:{" "}
          <a className="text-indigo-600 underline" href={LINKS.afipFe} target="_blank" rel="noreferrer">
            AFIP Factura Electrónica
          </a>
          {" · "}
          <a className="text-indigo-600 underline" href={LINKS.wsfeHomo} target="_blank" rel="noreferrer">
            WSDL WSFE homologación
          </a>
          {" · "}
          <a className="text-indigo-600 underline" href={LINKS.wsfeProd} target="_blank" rel="noreferrer">
            WSDL WSFE producción
          </a>
          .
        </p>
      </div>

      {variant === "firstRun" && (
        <p className="text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded p-2">
          Usamos el CUIT del paso anterior solo para probar conexión y listar PV antes de crear la empresa en la base.
          Después el CUIT sale de <strong>Empresa</strong> (CompanyConfig).
        </p>
      )}

      <p className="text-xs text-slate-500">
        Criterio: un <strong>punto de venta fiscal</strong> por instalación suele ser suficiente; no uses un PV AFIP por
        cada vendedor — la trazabilidad interna es con usuarios, cajas y sucursales en la app.
      </p>

      {snapForTab && (
        <div className="grid gap-1 text-xs font-mono bg-white dark:bg-slate-950 border rounded p-2">
          <span>Cert: {snapForTab.certPresent ? "presente" : "ausente"}</span>
          <span>Clave: {snapForTab.keyPresent ? "presente" : "ausente"}</span>
          <span>Origen: {snapForTab.usingUploadedCert ? "subido (media)" : "archivos en carpeta arca/ (legado)"}</span>
          {snapForTab.certNotAfter && <span>Vencimiento cert. (aprox.): {snapForTab.certNotAfter}</span>}
          <span>CUIT resuelto: {snapForTab.cuit || "—"}</span>
        </div>
      )}

      <div className="space-y-2">
        <Label>Entorno operativo activo (facturación CAE)</Label>
        <select
          className="w-full border rounded-md h-9 px-2 bg-background text-sm"
          value={activeEnv}
          onChange={(e) => setActiveEnv(e.target.value as ArcaEnvironment)}
        >
          <option value="homologacion">Homologación / test</option>
          <option value="produccion">Producción</option>
        </select>
        {envInUseCaE != null && activeEnv !== envInUseCaE && (
          <p className="text-xs text-amber-800 dark:text-amber-200">
            Elegiste otro entorno; el CAE sigue usando <strong>{envInUseCaE === "produccion" ? "producción" : "homologación"}</strong> hasta
            que pulses <strong>Guardar entorno activo y punto de venta</strong>.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Punto de venta fiscal (WSFE) — pestaña {tab === "homologacion" ? "homologación" : "producción"}</Label>
        <NumberField
          min={1}
          value={puntoVenta}
          onChange={(e) => setPuntoVenta(parseInt(e.target.value, 10) || 1)}
        />
        <p className="text-xs text-slate-500">
          Debe coincidir con un PV habilitado en AFIP para este CUIT. Si no tenés uno, crealo en el sitio de AFIP/ARCA.
        </p>
      </div>

      <Button type="button" size="sm" onClick={savePuntoYActivo} disabled={busy}>
        Guardar entorno activo y punto de venta
      </Button>

      <div className="border-t pt-3 space-y-2">
        <Label>Subir certificado (.crt/.pem) y clave privada (.key) — {tab}</Label>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input type="file" accept=".crt,.pem,.cer" onChange={(e) => setCertFile(e.target.files?.[0] || null)} />
          <Input type="file" accept=".key" onChange={(e) => setKeyFile(e.target.files?.[0] || null)} />
        </div>
        <Button type="button" size="sm" variant="secondary" onClick={upload} disabled={busy}>
          Subir archivos
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 border-t pt-3">
        <Button type="button" size="sm" variant="outline" onClick={validate} disabled={busy}>
          Validar conexión (WSAA / WSFE)
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={listarPv} disabled={busy}>
          Listar PV en AFIP (FEParamGetPtosVenta)
        </Button>
      </div>

      {ptosList.length > 0 && (
        <div className="border rounded p-2 text-xs max-h-40 overflow-y-auto">
          <p className="font-medium mb-1">Puntos de venta devueltos por AFIP</p>
          <ul className="space-y-1">
            {ptosList.map((p, i) => (
              <li key={i}>
                Nº {p.nro} — {p.emisionTipo || "?"} — bloqueado: {p.bloqueado || "?"}
              </li>
            ))}
          </ul>
          <Button
            type="button"
            size="sm"
            variant="link"
            className="h-auto p-0 mt-2"
            onClick={() => {
              const first = ptosList.find((x) => x.nro != null);
              if (first?.nro != null) {
                setPuntoVenta(Number(first.nro));
                toast.message(`Punto de venta ${first.nro} copiado al campo; guardá con "Guardar entorno…".`);
              }
            }}
          >
            Usar el primero de la lista como número sugerido
          </Button>
        </div>
      )}

      {variant === "firstRun" && (
        <p className="text-xs text-slate-500">
          Podés continuar sin subir certificados ahora y completar todo en <strong>Opciones → ARCA / AFIP</strong> después
          del primer inicio de sesión.
        </p>
      )}
    </div>
  );
}
